<script setup lang="ts">
    import { computed, toRef } from 'vue'
    import type { ProjectPlanPickerDialogViewModel } from "@Quest/PortfolioScenarios/Components/ProjectPlanPickerDialog"

    type PlanItem = { Id: string; Name: string; CreatedAt?: string }

    // props
    const props = defineProps<{
        viewModel: ProjectPlanPickerDialogViewModel
    }>()

    // emits
    const emit = defineEmits<{
        (e: 'confirmed', payload: { projectId: string; lockedPlanId: string | null }): void
    }>()

    // vm shortcut
    const vm = toRef(props, 'viewModel')

    // state
    const opened = computed(() => !!vm.value?.opened)
    const initialized = computed(() => !!vm.value.initialized)

    const projectName = computed(() => vm.value.projectName || 'Project')
    const plans = computed<PlanItem[]>(() => vm.value.plans as unknown as PlanItem[])
    const activePlanId = computed(() => vm.value.activePlanId)

    const lockedPlanId = computed<string | null>({
        get: () => vm.value.lockedPlanId,
        set: v => vm.value.lockedPlanId = v
    })

    const canConfirm = computed(() => initialized.value)

    // derived list
    const orderedPlans = computed<PlanItem[]>(() =>
    {
        const arr = [...plans.value]
        const active = activePlanId.value
            ? arr.find(p => p.Id === activePlanId.value)
            : undefined

        const rest = arr.filter(p => p.Id !== activePlanId.value)

        rest.sort((a, b) =>
        {
            const ad = a.CreatedAt ? Date.parse(a.CreatedAt) : NaN
            const bd = b.CreatedAt ? Date.parse(b.CreatedAt) : NaN
            if (!Number.isNaN(ad) && !Number.isNaN(bd)) return bd - ad
            if (!Number.isNaN(ad)) return -1
            if (!Number.isNaN(bd)) return 1
            return a.Name.localeCompare(b.Name)
        })

        return active ? [active, ...rest] : rest
    })

    // actions
    function toggleLock(id: string)
    {
        lockedPlanId.value = lockedPlanId.value === id ? null : id
    }

    function resetToDefault()
    {
        lockedPlanId.value = null
    }

    function handleConfirm()
    {
        emit('confirmed', {
            projectId: vm.value.projectId,
            lockedPlanId: lockedPlanId.value
        })
    }

    function close()
    {
        vm.value.close()
    }
</script>


<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-dialog-scrollable modal-lg">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Lock a plan version: {{ projectName }}</h5>
                        <button @click="close" type="button" class="close" aria-label="Close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <!-- Loading -->
                        <div v-if="!initialized" class="text-center my-5">
                            <h5>
                                <i class="fa fa-spinner fa-spin mr-2"></i>
                                Loading plans...
                            </h5>
                        </div>

                        <!-- Loaded -->
                        <div v-else>
                            <div class="alert alert-info mb-3" role="alert">
                                By default, this scenario always follows the project’s current <em>Active</em> plan.
                                You can lock it to a specific version below if needed.
                                <button v-if="lockedPlanId !== null"
                                        type="button"
                                        class="btn btn-link p-0 align-baseline"
                                        @click="resetToDefault">
                                    Reset to default
                                </button>
                            </div>

                            <div class="list-group">
                                <button v-for="p in orderedPlans"
                                        :key="p.Id"
                                        type="button"
                                        class="list-group-item list-group-item-action d-flex justify-content-between align-items-center plan-row"
                                        @click="toggleLock(p.Id)"
                                        :aria-pressed="lockedPlanId === p.Id"
                                        :title="p.Name">
                                    <span class="d-flex align-items-center">
                                        <i class="mr-2"
                                           :class="lockedPlanId === p.Id ? 'fa-solid fa-circle-check text-primary' : 'fa-regular fa-circle text-muted'"
                                           aria-hidden="true"></i>
                                        <span :class="{ 'font-weight-semibold': lockedPlanId === p.Id }">
                                            {{ p.Name }}
                                        </span>
                                    </span>
                                    <span v-if="p.Id === activePlanId" class="badge badge-success">Active</span>
                                </button>

                                <div v-if="orderedPlans.length === 0" class="list-group-item text-muted small">
                                    No plans found for this project.
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="modal-footer">
                        <button @click="close" type="button" class="btn btn-outline-secondary">Cancel</button>
                        <button @click="handleConfirm" type="button" class="btn btn-primary" :disabled="!canConfirm">
                            Confirm
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
    .font-weight-semibold {
        font-weight: 600;
    }

    .plan-row {
        padding-top: .6rem;
        padding-bottom: .6rem;
    }
</style>
